import DeletedActivitiesCollection from '@polleverywhere/core/lib/models/deleted_activities_collection'
import Groups from '@polleverywhere/core/lib/models/groups'
import ActivityCollection from '@polleverywhere/core/lib/models/activity_collection'
import Discourse from '@polleverywhere/core/lib/models/discourse'
import SharedGroups from '@polleverywhere/core/lib/models/shared_groups'
import ErrorModel from '../../models/error'
import Skeleton from '../../models/skeleton'
import getStorage from '../../storage'
import fetchActivity from '../../util/fetch_activity'
import populateSkeletons from '../../util/populate_skeletons'
import fetchGroups from '../../util/fetch_groups'
import getActivityIds from '../../util/get_activity_ids'
import store from '@polleverywhere/redux'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import { slice as appSlice } from '../../slices/app'
import Group from '@polleverywhere/core/lib/models/group'

function getGroup (id) {
  const foldersEnabled = store.getState()[profileSlice.name].user?.folders_enabled

  if (foldersEnabled) {
    return new Group({ id })
  } else {
    return this.get('groups').get(id)
  }
}

function search (collection, term) {
  // abort last request
  collection._lastAjax && collection._lastAjax.abort()

  const opts = {
    url: '/api/activities/search',
    data: {
      term
    },
    add: false,
    merge: false,
    remove: false
  }

  const user = this.get('user')

  fetchActivities.call(this, collection, opts)
    .then((data) => {
      // cache group name for lookup
      const groups = new Groups(data.groups)

      const activities = []
      _.flatten(_.compact([data.polls, data.surveys, data.competitions])).forEach((obj) => {
        // Don't list poll in survey
        if (obj.survey_id) {
          return
        }

        // Don't list poll in competition
        if (obj.competition_id) {
          return
        }

        // Hack to filter out results that don't belong to the current user
        // TODO: remove this when the api is fixed
        if (user.id !== obj.owner_id) {
          return
        }

        // normalize group_id
        obj.group_id = obj.group_id || -1

        // attach the group name
        const group = groups.get(obj.group_id)
        if (group) {
          obj.group_name = group.get('name')
        }

        activities.push(obj)
      })

      return activities
    })
    .then((data) => {
      collection.set(data)
    })
}

function fetchActivities (collection, opts = {}) {
  // We only want to populate skeletons if we are going to be replacing all items in the collection.
  // Backbone defaults remove to true. If for whatever reason remove is set to false, then don't
  // populate skeletons
  opts = Object.assign({
    remove: true
  }, opts)

  if (collection.isEmpty() && opts.remove) {
    populateSkeletons(collection, { type: 'multiple_choice_poll' })
  }

  if (!this.get('selecting') || collection.where({ selected: true }).length === 0) {
    collection._lastAjax = collection.fetch(opts)
    return collection._lastAjax
  }
}

function createSearchCollection () {
  const collection = new ActivityCollection()

  // This comparator groups activities within the same group together
  collection.comparator = (model1, model2) => {
    if (model1.get('group_id') === model2.get('group_id')) {
      const title1 = model1.get('title') || ''
      const title2 = model2.get('title') || ''
      return title1.localeCompare(title2)
    }

    const group1 = model1.get('group_id').toString()
    const group2 = model2.get('group_id').toString()

    return group1.localeCompare(group2)
  }

  return collection
}

function clearSelected () {
  this.get('searchCollection').forEach((m) => {
    m.unset('selected')
  })

  this.get('groups').forEach((group) => {
    group.activities.forEach((activity) => {
      activity.unset('selected')
    })
  })

  this.get('sharedGroups').forEach((group) => {
    group.activities.forEach((activity) => {
      activity.unset('selected')
    })
  })

  this.get('trashCollection').forEach((m) => {
    m.unset('selected')
  })
}

function computePresenterMode () {
  let presenterMode

  if (store.getState()[appSlice.name].mode === 'integration') {
    presenterMode = 'insert'
  } else if (this.get('externalScreenConnected')) {
    presenterMode = 'remote'
  } else {
    presenterMode = 'projector'
  }

  this.set({
    presenterMode
  })
}

export default class State extends Backbone.Model {
  get defaults () {
    return {
      user: null,
      page: null, // group | search | trash | presenter
      pageScrollPosition: 0, // track current page scroll position
      pageAction: null, // page level actions like select, reorder, create, edit

      // activity browser
      groups: null,
      sharedGroups: null,
      group: null,
      activity: null,

      searchTerm: '',
      searchCollection: createSearchCollection(),

      trashCollection: new DeletedActivitiesCollection(),

      // activity bulk actions
      selecting: false,
      bulkAction: null,

      // integration is for when the app is in native container
      mode: 'standalone', // standalone | integration

      // these are propagated from the app state model
      externalScreenConnected: false,

      // computed value
      presenterMode: 'projector',

      checkable: true,
      showTrash: true
    }
  }

  initialize (attrs = {}, options = {}) {
    if (!this.has('groups')) {
      // Force the groups sequence
      this.get('user').groups.comparator = 'sequence'
      this.set({ groups: this.get('user').groups })
    }

    if (!this.has('sharedGroups')) {
      this.set('sharedGroups', new SharedGroups())
    }

    // Initialize ungrouped after collection listeners are active.
    this.get('groups').add({ id: -1 })

    this.on('change:selecting', (model, selecting) => {
      if (!selecting) {
        clearSelected.call(this)
      }
    })

    // fetch new groups when user changes
    this.listenTo(this.get('user'), 'change:id', (user, id) => {
      if (id) {
        const opts = {}
        // fetch shared activities data
        if (user.get('features') && user.get('features').shared_activities) {
          this.get('sharedGroups').fetch()
          opts.data = { include_shared_activity_groups: true }
        }

        fetchGroups(this.get('groups'), opts)
      }
    })

    // data fetches when page changes
    this.on('change:page', (model, page, options = {}) => {
      // reset page scroll position
      this.set({ pageScrollPosition: 0 })

      switch (page) {
        case 'group':
          if (!this.get('group')) {
            this.set({ group: this.get('groups').get(-1) })
          }
          break

        case 'trash': {
          const activities = this.get('trashCollection')
          fetchActivities.call(this, activities)
          break
        }

        case 'search': {
          const term = this.get('searchTerm')
          const collection = this.get('searchCollection')

          if (term && term.trim().length > 0) {
            search.call(this, collection, term)
          }
          break
        }
      }
    })

    this.on('change:activity', async (model, activity, options = {}) => {
      if (!activity) {
        return
      }

      if (activity instanceof Skeleton) {
        return
      }

      if (activity instanceof ErrorModel) {
        return
      }

      _.defaults(options, {
        fetch: true
      })

      if (options.fetch) {
        activity = await fetchActivity(activity)

        if (activity instanceof ErrorModel) {
          this.changeActivity(activity)
        } else {
          activity.instance.start()
        }
      } else {
        activity.instance.start()
      }

      const prevActivity = this.previous('activity')
      prevActivity && prevActivity.instance && prevActivity.instance.stop()
    })

    // when changing groups
    this.on('change:group', (model, group, options = {}) => {
      _.defaults(options, {
        fetch: true
      })

      if (options.fetch && group.id >= -1) {
        if (group.isNew()) {
          group.on('change:id', () => {
            this.fetchGroupActivities(group, options)
          })
        } else {
          this.fetchGroupActivities(group, options)
        }
      }
    })

    // Clear immediately
    this.on('change:searchTerm', (model, term) => {
      const collection = this.get('searchCollection')

      if (!term || term.trim().length === 0) {
        if (collection.isEmpty()) {
          // If the collection is already empty, an 'update'
          // event must be manually triggered so the collection
          // can recompute the messaging
          collection.trigger('update', this.collection, {})
        } else {
          collection.set([])
        }

        return
      }

      populateSkeletons(collection, { type: 'multiple_choice_poll' })
    })

    // Debounced search
    this.on('change:searchTerm', _.debounce(() => {
      const term = this.get('searchTerm')
      if (term && term.trim().length > 0) {
        const collection = this.get('searchCollection')
        search.call(this, collection, term)
      }
    }, 500))

    this.on('change:externalScreenConnected', computePresenterMode.bind(this))
    computePresenterMode.call(this)

    this.on('change:pageAction', (_, action) => {
      clearInterval(this.discourseFetch)

      if (action === 'moderate') {
        const model = this.get('activity')

        // initial fetch
        model.results.fetch({ batched: false })

        if (model instanceof Discourse) {
          this.discourseFetch = setInterval(() => {
            model.results.fetch()
          }, 3000)
        }
      }
    })
  }

  startAction (action, opts = {}) {
    this.set({ pageAction: action }, opts)
  }

  finishAction (opts = {}) {
    // always redirect actions back to activities page when folders is on
    const attributes = { pageAction: null }
    if (store.getState()[profileSlice.name].user.folders_enabled) {
      attributes.page = 'activities'
    }

    this.set(attributes, opts)
  }

  changePage (page, overrides = {}, opts = {}) {
    _.defaults(overrides, {
      page: page,
      selecting: false,
      bulkAction: null,
      pageAction: null
    })

    this.set(overrides, opts)

    // Manually fetch if the group hasn't changed
    if (!this.hasChanged('group') && opts.fetch) {
      this.fetchGroupActivities(this.get('group'))
    }
  }

  changeActivity (activity, overrides = {}, opts = {}) {
    // Can't select activity while doing bulk actions,
    if (this.get('bulkAction')) {
      return
    }

    // Can't change activity when on trash page
    if (this.get('page') === 'trash') {
      return
    }

    _.defaults(overrides, {
      activity,
      pageAction: null
    })

    if (activity) {
      const groupId = activity.get('group_id') || -1

      // check backbone and redux groups
      overrides.group = getGroup.call(this, groupId)

      // otherwise, check shared groups
      if (!overrides.group) {
        overrides.group = this.get('sharedGroups').get(groupId)
      }
    }

    this.set(overrides, opts)
  }

  changeGroup (group, overrides = {}, opts = {}) {
    // Can't change group when doing a bulk action
    if (this.get('bulkAction')) { return }

    // Change page if we're currently not on the group page
    if (this.get('page') !== 'group') {
      _.defaults(overrides, { group })
      this.changePage('group', overrides, opts)
      return
    }

    _.defaults(overrides, {
      pageScrollPosition: 0,
      group,
      pageAction: null
    })

    this.set(overrides, opts)
  }

  fetchGroupActivities (group, options = {}) {
    options = Object.assign({
      data: {
        group_id: group.id
      }
    }, options)

    return fetchActivities.call(this, group.activities, options)
  }

  search (term) {
    this.set({ searchTerm: term })
  }

  clearSearch () {
    this.set({ searchTerm: '' })
  }

  startSelecting () {
    this.set({
      selecting: true
    })
  }

  startBulkAction (action) {
    this.set({
      bulkAction: action
    })
  }

  stopBulkAction () {
    this.set({
      bulkAction: null
    })
  }

  finishBulkAction () {
    this.set({
      bulkAction: null,
      selecting: false
    })
  }

  getSelectedActivities (page = this.get('page')) {
    // selected activities are stored differently with folders flag on
    if (store.getState()[profileSlice.name].user.folders_enabled) {
      return this.get('selected')
    } else {
      switch (this.get('page')) {
        case 'trash':
          return this.get('trashCollection').where({ selected: true })

        case 'search':
          return this.get('searchCollection').where({ selected: true })

        case 'group': {
          let activities = []
          const shared = this.get('group').get('shared_activity_groups') &&
            !!this.get('group').get('shared_activity_groups').length &&
            this.get('group').get('owner_id') !== this.get('user').id

          if (shared) {
            // if in shared group, only get the activities in shared groups
            this.get('sharedGroups').forEach((group) => {
              activities = activities.concat(group.activities.where({ selected: true }))
            })
          } else {
            // if in owned group, only get own activities
            this.get('groups').forEach((group) => {
              activities = activities.concat(group.activities.where({ selected: true }))
            })
          }

          return activities
        }
      }
    }
  }

  getSelectedActivityIds () {
    return getActivityIds(this.getSelectedActivities())
  }

  messageHistory () {
    return getStorage(this.get('user'), 'message')
  }

  inspect () {
    const result = super.toJSON()

    // Leaving this info out until it proves useful, so as to not be overwhelming
    delete result.user
    delete result.groups
    delete result.searchCollection
    delete result.sharedGroups
    delete result.trashCollection

    return result
  }
}
